import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef as MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IBeneficiaryComissionFormDialogData } from '../../../models/dialog';
import { ApiContractService } from '../../../services/api/api-contract.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { ContratoBeneficiarioFormComponent } from '../contrato-beneficiario-form/contrato-beneficiario-form.component';

@Component({
  selector: 'app-contrato-editar-comissao',
  templateUrl: './contrato-editar-comissao.component.html',
  styleUrls: ['./contrato-editar-comissao.component.scss']
})
export class ContratoEditarComissaoComponent implements OnInit {
  readonly fixedCommissionType = 1;
  readonly variableCommissionType = 2;
  specialRate: number | undefined;
  hasSubstitutedBeneficiary: boolean = false;
  isFixedType: boolean = false;
  fixedMasterTransferValue: any = 0;
  fixedSubstitutedTransferValue: any = 0;
  variableMasterCommissionValue: string = '';
  variableSubstitutedCommissionValue: string = '';
  commissionEditForm: FormGroup;
  agreementDocumentFile: any;
  submitted: boolean = false;

  constructor(
    private _dialogRef: MatDialogRef<ContratoBeneficiarioFormComponent>,
    private fb: FormBuilder,
    private _snackbarService: SnackbarService,
    private _apiContractService: ApiContractService,
    @Inject(MAT_DIALOG_DATA)
    public parentData: IBeneficiaryComissionFormDialogData
  ) {
    this.commissionEditForm = this.fb.group({
      commissionType: [this.variableCommissionType, [Validators.required]],
      masterPartnerCommission: ['', [Validators.required]],
      substitutedPartnerCommission: [''],
      agreementDocument: [null, Validators.required],
      justification: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.hasSubstitutedBeneficiary = this.parentData.hasSubstitutedBeneficiary;
    this._apiContractService
      .getContractChangeCommission(this.parentData.contractId)
      .subscribe({
        next: (response) => {
          const comission = response;
          const selectedCommissionType =
            Number(comission.CommissionType) === this.fixedCommissionType
              ? this.fixedCommissionType
              : this.variableCommissionType;

          this.isFixedType = selectedCommissionType === this.fixedCommissionType;

          this.fixedMasterTransferValue = this.parseNumericValue(
            comission.BeneficiaryAmount
          );

          this.fixedSubstitutedTransferValue = this.parseNumericValue(
            comission.SubstitutedBeneficiaryAmount
          );

          if (!this.isFixedType) {
            comission.MasterPartnerCommission = this.normalizePercent(
              comission.MasterPartnerCommission,
              true
            );
            comission.SubstitutedPartnerCommission = this
              .hasSubstitutedBeneficiary
              ? this.normalizePercent(
                  comission.SubstitutedPartnerCommission,
                  true
                )
              : '';

            this.variableMasterCommissionValue =
              comission.MasterPartnerCommission ?? '';
            this.variableSubstitutedCommissionValue =
              comission.SubstitutedPartnerCommission ?? '';
          } else {
            this.fixedMasterTransferValue = this.parseNumericValue(
              comission.BeneficiaryAmount ?? comission.MasterPartnerCommission
            );

            this.fixedSubstitutedTransferValue = this.parseNumericValue(
              comission.SubstitutedBeneficiaryAmount ?? comission.SubstitutedPartnerCommission
            );
          }

          if (!this.isFixedType && comission.SpecialRateTransfer != '0') {
            this.specialRate = comission.SpecialRateTransfer;
          }

          this.commissionEditForm.patchValue({
            commissionType: selectedCommissionType,
            masterPartnerCommission: this.isFixedType
              ? this.fixedMasterTransferValue
              : this.variableMasterCommissionValue,
            substitutedPartnerCommission:
              this.isFixedType
                ? this.fixedSubstitutedTransferValue
                : this.variableSubstitutedCommissionValue,
            justification: this.parentData.justificationCommission
          });

          this.applyCommissionTypeRules(selectedCommissionType);
        }
      });
  }

  onCommissionTypeChange(typeValue: string): void {
    const selectedType = Number(typeValue);
    this.applyCommissionTypeRules(selectedType);
  }

  private applyCommissionTypeRules(selectedType: number): void {
    const wasFixedType = this.isFixedType;
    this.isFixedType = selectedType === this.fixedCommissionType;

    const masterControl = this.commissionEditForm.get('masterPartnerCommission');
    const substitutedControl = this.commissionEditForm.get(
      'substitutedPartnerCommission'
    );

    if (!wasFixedType && this.isFixedType) {
      this.variableMasterCommissionValue = String(masterControl?.value ?? '');
      this.variableSubstitutedCommissionValue = String(
        substitutedControl?.value ?? ''
      );
      masterControl?.setValue(this.fixedMasterTransferValue);

      if (this.hasSubstitutedBeneficiary) {
        substitutedControl?.setValue(this.fixedSubstitutedTransferValue);
      }
    } else if (wasFixedType && !this.isFixedType) {
      this.fixedMasterTransferValue = masterControl?.value ?? this.fixedMasterTransferValue;

      if (this.hasSubstitutedBeneficiary) {
        this.fixedSubstitutedTransferValue =
          substitutedControl?.value ?? this.fixedSubstitutedTransferValue;
      }

      masterControl?.setValue(this.variableMasterCommissionValue || '');
      substitutedControl?.setValue(
        this.hasSubstitutedBeneficiary
          ? this.variableSubstitutedCommissionValue || ''
          : ''
      );
    } else if (this.isFixedType) {
      masterControl?.setValue(this.fixedMasterTransferValue);

      if (this.hasSubstitutedBeneficiary) {
        substitutedControl?.setValue(this.fixedSubstitutedTransferValue);
      }
    }

    masterControl?.setValidators([Validators.required]);

    if (this.hasSubstitutedBeneficiary) {
      substitutedControl?.setValidators([Validators.required]);
    } else {
      substitutedControl?.setValue('');
      substitutedControl?.clearValidators();
    }

    masterControl?.updateValueAndValidity();
    substitutedControl?.updateValueAndValidity();
  }

  private normalizeDecimalValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    let normalizedValue = String(value)
      .trim()
      .replace(/[^0-9,.-]/g, '');

    if (normalizedValue.includes(',') && normalizedValue.includes('.')) {
      normalizedValue = normalizedValue.replace(/\./g, '').replace(',', '.');
    } else {
      normalizedValue = normalizedValue.replace(',', '.');
    }

    return normalizedValue;
  }

  hasError(controlName: string, errorName: string): boolean {
    const control = this.commissionEditForm.get(controlName);
    return (
      !!control &&
      ((control.touched && this.submitted) || this.submitted) &&
      control.errors &&
      control.errors[errorName]
    );
  }

  onFileSelected(event: any): void {
    if (event.target.files && event.target.files.length) {
      const target = event.target as HTMLInputElement;
      const { file } = this._apiContractService.validateFile(target, true);
      this.agreementDocumentFile = file;
    }
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.commissionEditForm.valid) {
      const formValue = this.commissionEditForm.value;
      const formData = new FormData();
      const selectedCommissionType = Number(formValue.commissionType);
      const isFixedCommissionType =
        selectedCommissionType === this.fixedCommissionType;

      formData.append(
        'commissionType',
        String(selectedCommissionType)
      );

      if (isFixedCommissionType) {
        const masterCommissionValue = formValue.masterPartnerCommission;
        const normalizedMasterCommission = this.normalizeDecimalValue(
          masterCommissionValue
        );

        const substitutedCommissionValue = this.hasSubstitutedBeneficiary
          ? formValue.substitutedPartnerCommission
          : 0;
        const normalizedSubstitutedCommission = this.normalizeDecimalValue(
          substitutedCommissionValue
        );

        formData.append(
          'masterPartnerCommission',
          String(normalizedMasterCommission || 0)
        );
        formData.append(
          'masterPartnerCommissionString',
          String(normalizedMasterCommission || 0)
        );
        formData.append(
          'substitutedPartnerCommission',
          String(normalizedSubstitutedCommission || 0)
        );
        formData.append(
          'substitutedPartnerCommissionString',
          String(normalizedSubstitutedCommission || 0)
        );
      } else {
        const masterCommissionString = formValue.masterPartnerCommission
          .trim()
          .replace(/\s*%\s*/g, '');

        const substitutedCommissionString = formValue.substitutedPartnerCommission
          ? formValue.substitutedPartnerCommission.trim().replace(/\s*%\s*/g, '')
          : '';

        const normalizedMasterCommission = masterCommissionString.replace(
          ',',
          '.'
        );
        const normalizedSubstitutedCommission = substitutedCommissionString
          ? substitutedCommissionString.replace(',', '.')
          : '';

        formData.append('masterPartnerCommission', normalizedMasterCommission);
        formData.append('masterPartnerCommissionString', masterCommissionString);
        formData.append(
          'substitutedPartnerCommission',
          normalizedSubstitutedCommission
        );
        formData.append(
          'substitutedPartnerCommissionString',
          substitutedCommissionString
        );
      }

      formData.append('justification', formValue.justification);
      formData.append('file', this.agreementDocumentFile);

      this._apiContractService
        .updateContractCommission(this.parentData.contractId, formData)
        .subscribe({
          next: (result) => {
            if (result) {
              this._snackbarService.openSnackBar(
                'Os valores das comissões foram alterados com sucesso!',
                'success'
              );

              location.reload();
            }
          }
        });
    } else {
      const formControls = this.commissionEditForm.controls;
      for (const controlName in formControls) {
        if (formControls.hasOwnProperty(controlName)) {
          const control = formControls[controlName];
          if (control.errors) {
            control.markAsTouched();
          }
        }
      }

      this._snackbarService.openSnackBar(
        'O formulário deve ser preenchido corretamente!',
        'warning'
      );
    }
  }

  closeDialogCommissionEdit(): void {
    this._dialogRef.close();
  }

  normalizePercent(percentValue: any, format: boolean): any {
    let normalizedValue: any = percentValue.toString();
    normalizedValue = format
      ? normalizedValue.replace('.', ',') + ' %'
      : normalizedValue.replace(',', '.').replace(' %', '');
    return normalizedValue;
  }

  private parseNumericValue(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    const normalized = String(value)
      .trim()
      .replace(/\./g, '')
      .replace(',', '.');

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}
